import gql from 'graphql-tag';
import {
  ActionPlanSchema,
  ActionPlanTemplateTasksSchema,
} from '../../definitions/schemas';
import { ActionPlanVisibility } from '../../enums/ActionPlans';
import Interface from '../Interface';
import { actionPlanFields } from './ActionPlanFields';

interface CreateActionPlanInput {
  actionPlanTemplateId?: string;
  description?: string;
  name: string;
  tasks?: ActionPlanTemplateTasksSchema;
  visibility: ActionPlanVisibility;
}

interface CreateActionPlanData {
  createActionPlan: {
    actionPlan: ActionPlanSchema;
  };
}

interface UpdateActionPlanInput
  extends Omit<CreateActionPlanInput, 'actionPlanTemplateId' | 'tasks'> {
  id: string;
}

interface UpdateActionPlanData {
  updateActionPlan: {
    actionPlan: ActionPlanSchema;
  };
}

interface DeleteActionPlanInput {
  id: string;
}

interface GetActionPlanInput {
  id: string;
}

interface GetActionPlanData {
  getActionPlan: ActionPlanSchema;
}

export default class ActionPlansInterface extends Interface {
  public async create(input: CreateActionPlanInput) {
    return this.mutate<CreateActionPlanData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation createActionPlan($input: CreateActionPlanInput!) {
            createActionPlan(input: $input) {
              errors
              message
              actionPlan {
                ${actionPlanFields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: response.data,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error creating action plan');
  }

  public async update(input: UpdateActionPlanInput) {
    return this.mutate<UpdateActionPlanData>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation updateActionPlan($input: UpdateActionPlanInput!) {
            updateActionPlan(input: $input) {
              errors
              message
              actionPlan {
                ${actionPlanFields}
              }
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: response.data,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error updating action plan');
  }

  public async delete(input: DeleteActionPlanInput) {
    return this.mutate<null>(async () => {
      const response = await this.apolloService.executeMutation({
        mutation: gql`
          mutation deleteActionPlan($input: DeleteActionPlanInput!) {
            deleteActionPlan(input: $input) {
              errors
              message
            }
          }
        `,
        variables: {
          input,
        },
      });

      return {
        data: null,
        serverErrors: response.errors,
        serverMessage: response.errors?.map((e) => e.message).join(''),
      };
    }, 'Error deleting action plan');
  }

  public async get(variables: GetActionPlanInput, fields = actionPlanFields) {
    const response = await this.query<GetActionPlanData>(
      () =>
        this.apolloService.executeQuery<GetActionPlanData>({
          query: gql`
            query getActionPlan($id: ID!) {
              getActionPlan(id: $id) {
                ${fields}
              }
            }
          `,
          variables,
        }),
      'Error getting action plan'
    );

    return response.getActionPlan;
  }
}
